import { useMemo } from "react";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { useActivities } from "./useActivities";
import { PageLoader, StatTile, Progress } from "@/components/ui";
import { HealthBadge } from "@/components/badges";
import { HEALTH_META, money, pct, fmtDate } from "@/lib/format";
import type { ProjectBundle } from "../ProjectDetail";

export function Overview({ bundle }: { bundle: ProjectBundle }) {
  const { data: activities, isLoading } = useActivities(bundle.project.id);
  const p = bundle.project;

  const stats = useMemo(() => {
    const list = activities ?? [];
    const counts: Record<string, number> = { on_track: 0, ahead: 0, delayed: 0, cancelled: 0, planned: 0, done: 0 };
    for (const a of list) counts[a.scheduleHealth]++;
    const byPhase = bundle.phases.map((ph) => {
      const items = list.filter((a) => a.phaseId === ph.id);
      const wSum = items.reduce((s, a) => s + (a.weightage || 0), 0);
      const wProg = items.reduce((s, a) => s + a.weightedProgress, 0);
      return { name: ph.name, count: items.length, percent: wSum > 0 ? (wProg / wSum) * 100 : 0 };
    }).filter((x) => x.count > 0);
    const overdue = list.filter((a) => a.scheduleHealth === "delayed").sort((a, b) => (a.endDate ?? "").localeCompare(b.endDate ?? ""));
    return { counts, byPhase, overdue };
  }, [activities, bundle.phases]);

  if (isLoading) return <PageLoader />;

  const pieData = Object.entries(stats.counts).filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: HEALTH_META[k as keyof typeof HEALTH_META].label, value: v, color: HEALTH_META[k as keyof typeof HEALTH_META].color }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Overall progress" value={pct(p.overallPercent)} accent="rgb(var(--brand-600))" />
        <StatTile label="Budget used" value={money(p.actualCost, p.currency)} sub={`of ${money(p.plannedBudget, p.currency)}`} />
        <StatTile label="Delayed" value={stats.counts.delayed} accent={stats.counts.delayed ? "rgb(var(--st-delayed))" : undefined} />
        <StatTile label="Timeline" value={`${fmtDate(p.startDate)}`} sub={`→ ${fmtDate(p.endDate)}`} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card p-4">
          <h3 className="mb-2 text-sm font-semibold">Activity health</h3>
          <div className="h-48">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={68} paddingAngle={2}>
                  {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2">
            {pieData.map((d) => <span key={d.name} className="chip text-[11px]" style={{ background: `${d.color}18`, color: d.color }}>{d.name} {d.value}</span>)}
          </div>
        </div>

        <div className="card p-4 lg:col-span-2">
          <h3 className="mb-3 text-sm font-semibold">Progress by phase</h3>
          <div className="space-y-2.5">
            {stats.byPhase.length === 0 && <p className="text-sm text-ink-faint">No activities yet. <Link to="schedule" className="text-brand-600">Build the schedule →</Link></p>}
            {stats.byPhase.map((ph) => (
              <div key={ph.name}>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="font-medium">{ph.name} <span className="text-ink-faint">({ph.count})</span></span>
                  <span className="tabular-nums">{pct(ph.percent)}</span>
                </div>
                <Progress value={ph.percent} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {p.healthNote && (
        <div className="card border-l-4 border-l-accent p-4">
          <h3 className="mb-1 text-sm font-semibold">Manager's status note</h3>
          <p className="whitespace-pre-wrap text-sm text-ink-soft">{p.healthNote}</p>
        </div>
      )}

      <div className="card p-0">
        <h3 className="border-b border-line px-4 py-3 text-sm font-semibold">Delayed activities</h3>
        <div className="divide-y divide-line">
          {stats.overdue.length === 0 && <p className="px-4 py-6 text-sm text-ink-faint">Nothing delayed.</p>}
          {stats.overdue.map((a) => (
            <Link key={a.id} to={`schedule?activity=${a.id}`} className="flex items-center justify-between px-4 py-2.5 hover:bg-surface-2/50">
              <span><span className="block text-sm font-medium">{a.title}</span>
                <span className="text-xs text-ink-faint">{a.responsibleUserName || a.responsibleRole || "Unassigned"} · due {fmtDate(a.endDate)}</span></span>
              <HealthBadge health={a.scheduleHealth} />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
